/**
 * 재고관리 (Phase 7-2) — mockup index.html stock 페이지 정합.
 *
 * 탭 5개 (2026-07-27): 주요재고현황 / 출고 관리 / 제조 / 품명&품목 / 입출고내역
 *   + 6번째 「주간 확인」(v2.202.0) — 매주 HDD·SSD 실물 확인 이력 (읽기 전용 · StockCheckHistoryTab)
 *   1. 주요재고현황 — v_stock 좌·우 표 (HDD / 기타) + 그룹 rowspan + 안전재고 미만 경고
 *   2. 품명&품목   — item_master 검색·필터 + 9컬럼 표 + 행 클릭 수정
 *
 * 권한:
 *   - 모든 권한 : 양쪽 탭 조회 (재고는 전직원 열람 — 도메인 규칙)
 *   - sales+admin : + 주요재고 추가 / + 품목 추가 / 품목 수정
 *
 * 행 클릭:
 *   - 탭 1 : 같은 품목 최근 10건 입·출고 내역 (PurchaseDetailModal 'stock')
 *   - 탭 2 : 행(현재고 외) → 품목 수정 폼 (sales+admin만) /
 *           현재고 칸 → 같은 품목 최근 입·출고 내역 (전직원)
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as stockApi from '../lib/stock-api';
import type { BreakdownKind } from '../lib/stock-api';
import * as stockReleaseApi from '../lib/stock-release-api';
import * as prefsApi from '../lib/preferences-api';
import type { ItemMasterRow, StockRow, UserPreferences } from '../lib/types';
import { useAuth } from '../lib/auth-context';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { SearchBar } from '../components/SearchBar';
import { ResetButton } from '../components/ResetButton';
import { ExcelModeGroup } from '../components/ExcelModeButton';
import { useExcelModeTable } from '../lib/useExcelModeTable';
import {
  type Column,
  ColumnHeader,
  applyColState,
  useColState,
} from '../components/DataTable';
import { DrillNum } from '../components/DrillNum';
import { MovementHistoryTab } from '../components/MovementHistoryTab';
import { ManufactureTab } from '../components/ManufactureTab';
import { MfgItemBadge } from '../components/MfgBadge';
import { StockCheckHistoryTab } from '../components/StockCheckHistoryTab';
import { StockAuditHistoryTab } from '../components/StockAuditHistoryTab';
import { PageTabs } from '../components/ui/PageTabs';
import { LoadingLines } from '../components/ui/Skeleton';

type ItemMasterColumn = Column<ItemMasterRow> & {
  defaultOn: boolean;
  toggleLabel?: string;
};

// 모달은 lazy — 초기 번들에서 분리. 행 클릭 / 등록·수정 버튼 클릭 시 처음 로드.
// (PurchaseDetailModal 은 PurchaseList 에서도 이미 lazy. 여기 정적 import 가 chunk 분리 무산 원인이었음.)
const PurchaseDetailModal = lazy(() =>
  import('../components/PurchaseDetailModal').then((m) => ({ default: m.PurchaseDetailModal })),
);
// 숫자 칸 클릭 드릴다운 + 그 안의 매출번호 클릭 (v2.78.0)
const StockBreakdownModal = lazy(() =>
  import('../components/StockBreakdownModal').then((m) => ({ default: m.StockBreakdownModal })),
);
const SalesSummaryModal = lazy(() =>
  import('../components/SalesSummaryModal').then((m) => ({ default: m.SalesSummaryModal })),
);
const KeyStockEditModal = lazy(() =>
  import('../components/KeyStockEditModal').then((m) => ({ default: m.KeyStockEditModal })),
);
const ItemFormModal = lazy(() =>
  import('../components/ItemFormModal').then((m) => ({ default: m.ItemFormModal })),
);
const StockReleaseFormModal = lazy(() =>
  import('../components/StockReleaseFormModal').then((m) => ({ default: m.StockReleaseFormModal })),
);
const StockReleaseReviewModal = lazy(() =>
  import('../components/StockReleaseReviewModal').then((m) => ({ default: m.StockReleaseReviewModal })),
);
const LeaseManagementModal = lazy(() =>
  import('../components/LeaseManagementModal').then((m) => ({ default: m.LeaseManagementModal })),
);
const InventoryAssetModal = lazy(() =>
  import('../components/InventoryAssetModal').then((m) => ({ default: m.InventoryAssetModal })),
);
const DeliveryConfirmationScanModal = lazy(() =>
  import('../components/DeliveryConfirmationScanModal').then((m) => ({ default: m.DeliveryConfirmationScanModal })),
);

type StockSubTab = 'stock' | 'release' | 'manufacture' | 'item' | 'movement' | 'check' | 'audit';

export function StockListPage() {
  // 탭은 URL(?tab=)과 맞물린다 (v2.127.0) — ⚠**지우지 말 것**. 사이드바 탭 문은 v2.135.0 에 없앴지만,
  //   대시보드 출고검수 링크(`/stock?review=1`)와 브라우저 탭 제목이 이 맞물림에 기댄다.
  //   주소 자체는 /stock 그대로.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const isStockTab = (v: string | null): v is StockSubTab =>
    v === 'stock' || v === 'release' || v === 'manufacture' || v === 'item' || v === 'movement' || v === 'check' || v === 'audit';
  const [tab, setTabState] = useState<StockSubTab>(isStockTab(tabParam) ? tabParam : 'stock');
  // URL → 화면 (사이드바에서 다른 탭 문을 눌렀을 때)
  useEffect(() => {
    if (isStockTab(tabParam) && tabParam !== tab) setTabState(tabParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);
  // 화면 → URL (탭 버튼을 눌렀을 때) — replace 라 뒤로가기 역사에 안 쌓인다
  const setTab = (t: StockSubTab) => {
    setTabState(t);
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', t);
    setSearchParams(sp, { replace: true });
  };
  const { user } = useAuth();
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const [adding, setAdding] = useState(false);
  const [creatingItem, setCreatingItem] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [managingLease, setManagingLease] = useState(false);
  const [viewingAsset, setViewingAsset] = useState(false);
  const [scanUpload, setScanUpload] = useState(false);

  // 대시보드 → /stock?review=1 진입 시 [출고 관리] 탭 + 출고검수 모달 자동 열기
  useEffect(() => {
    if (searchParams.get('review') === '1') {
      setTabState('release');
      setReviewing(true);
      // URL 정리 (모달 다시 안 뜨도록) — tab 은 남겨 사이드바가 [출고 관리]를 가리키게
      const sp = new URLSearchParams(searchParams);
      sp.delete('review');
      sp.set('tab', 'release');
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // 미승인 출고등록 카운트 — [✓ 출고검수] 버튼 빨간 뱃지
  const pendingQ = useQuery({
    queryKey: ['stock-releases', 'pending-count'],
    queryFn: () => stockReleaseApi.getPendingCount(),
    staleTime: 30_000,
  });
  const pendingCount = pendingQ.data?.pending ?? 0;

  // 재고는 있는데 주요재고로 안 잡혀 [주요재고현황] 에 안 나오는 품목 수 —
  // [⚙ 주요재고 수정] 버튼의 빨간 배지. 같은 queryKey 라 StockStatusTab 과 요청을 공유한다.
  const stockQ = useQuery({
    queryKey: ['stock'],
    queryFn: () => stockApi.listStock(),
    staleTime: 30_000,
  });
  const unregisteredCount = useMemo(
    () =>
      (stockQ.data ?? []).filter((s) => !s.isKeyStock && (s.currentStock ?? 0) > 0).length,
    [stockQ.data],
  );

  return (
    <div className="p-4 md:p-8">
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <h1 className="ui-page-title">재고관리</h1>
            <p className="ui-page-sub">
              {tab === 'stock'
                ? '주요재고현황 — 가용재고 = 지금 바로 출고할 수 있는 수량 (PURCHASE 자동 집계)'
                : tab === 'release'
                  ? '출고 관리 — 출고 등록·검수(승인)·임대 회수·납품확인증 스캔본을 한 곳에서'
                  : tab === 'manufacture'
                    ? '제조 — 자재를 조립해 새 완제품을 만들고, 원재료 원가를 완제품 원가로 합산'
                    : tab === 'item'
                      ? '품명&품목 마스터 — 매입관리대장 품명&품목 시트 기준'
                      : tab === 'check'
                        ? '주간 확인 이력 — 매주 HDD·SSD 실물 확인 기록 (읽기 전용 · 조치는 모바일 확인 화면에서)'
                        : tab === 'audit'
                          ? '실사·실물확인 — 분기 실사(분기마다 잔여재고 전 품목) + 🔎 수시 실물확인(연휴 전·연말 등 골라서) 기록·사진(NAS) · 세기는 모바일 앱에서 · 관리자는 여기서 취소 가능'
                          : '입출고내역 — 전체 품목의 입고·출고를 시간순으로 (검색·필터·정렬)'}
            </p>
            <LastUpdateBadge className="mt-1" />
          </div>
          {/* 출고등록·검수·임대·스캔본 버튼은 [출고 관리] 탭으로 이동 (2026-07-19 메뉴 재구성). */}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {tab === 'stock' && (
              <>
                {/* 주요재고 지정·해제·안전재고를 한 화면에서 (2026-07-28). 빨간 숫자 =
                    재고는 있는데 주요재고로 안 잡혀 목록에 안 나오는 품목 수. */}
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="btn btn-primary"
                >
                  ⚙ 주요재고 수정
                  {unregisteredCount > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-white text-rose-700 font-semibold">
                      미등록 {unregisteredCount}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setViewingAsset(true)}
                  className="btn btn-success-solid"
                  title="현재 보유 주요재고의 매입원가 총 합계"
                >
                  💰 보유재고 자산
                </button>
              </>
            )}
            {tab === 'item' && (
              <button
                type="button"
                onClick={() => setCreatingItem(true)}
                className="btn btn-primary"
              >
                + 품목 추가
              </button>
            )}
          </div>
        )}
      </div>

      {/* 탭 — 출고 관리의 빨간 배지 = 미승인(등록 상태) 출고 건수 */}
      <PageTabs
        tabs={[
          { key: 'stock', label: '주요재고현황' },
          { key: 'release', label: '출고 관리', badge: pendingCount },
          { key: 'manufacture', label: '제조' },
          { key: 'item', label: '품명&품목' },
          { key: 'movement', label: '입출고내역' },
          { key: 'check', label: '주간 확인' },
          { key: 'audit', label: '실사·실물확인' },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel="재고관리 보기"
        className="mb-3"
      />

      {tab === 'stock' && <StockStatusTab />}
      {tab === 'release' && (
        <ReleaseManagementTab
          pendingCount={pendingCount}
          onRelease={() => setReleasing(true)}
          onReview={() => setReviewing(true)}
          onLease={() => setManagingLease(true)}
          onScan={() => setScanUpload(true)}
        />
      )}
      {tab === 'manufacture' && <ManufactureTab />}
      {tab === 'item' && <ItemMasterTab canEdit={canEdit} />}
      {tab === 'movement' && <MovementHistoryTab />}
      {tab === 'check' && <StockCheckHistoryTab />}
      {tab === 'audit' && <StockAuditHistoryTab />}

      {/* lazy 모달 — 처음 열 때 동적 로드 */}
      <Suspense fallback={null}>
        {adding && (
          <KeyStockEditModal
            onClose={() => setAdding(false)}
            onSaved={() => setAdding(false)}
          />
        )}
        {creatingItem && (
          <ItemFormModal
            mode="create"
            onClose={() => setCreatingItem(false)}
            onSaved={() => setCreatingItem(false)}
          />
        )}
        {releasing && (
          <StockReleaseFormModal
            mode="create"
            onClose={() => setReleasing(false)}
            onSaved={() => setReleasing(false)}
          />
        )}
        {reviewing && (
          <StockReleaseReviewModal onClose={() => setReviewing(false)} />
        )}
        {managingLease && (
          <LeaseManagementModal onClose={() => setManagingLease(false)} />
        )}
        {viewingAsset && (
          <InventoryAssetModal onClose={() => setViewingAsset(false)} />
        )}
        {scanUpload && (
          <DeliveryConfirmationScanModal onClose={() => setScanUpload(false)} />
        )}
      </Suspense>
    </div>
  );
}

// ========================================================================
// 탭 — 출고 관리 (2026-07-19 메뉴 재구성: 모달 뒤에 숨어 있던 출고 업무를 상주 탭으로)
//   미승인(등록 상태) 출고가 항상 보이고, 등록·검수·임대·스캔본 진입 버튼을 모아둠.
//   각 액션의 권한은 기존 그대로 (모달 내부/백엔드에서 제한).
// ========================================================================
function ReleaseManagementTab({
  pendingCount,
  onRelease,
  onReview,
  onLease,
  onScan,
}: {
  pendingCount: number;
  onRelease: () => void;
  onReview: () => void;
  onLease: () => void;
  onScan: () => void;
}) {
  const q = useQuery({
    queryKey: ['stock-releases', 'recent'],
    queryFn: () => stockReleaseApi.listRecent(),
    staleTime: 30_000,
  });
  const rows = q.data ?? [];
  const pending = rows.filter((r) => r.status === '등록');
  const recentApproved = rows.filter((r) => r.status === '승인').slice(0, 10);

  return (
    <div className="space-y-4">
      {/* 액션 버튼 — 기존 주요재고현황 헤더에서 이동 */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onRelease}
          className="btn btn-primary"
        >
          📦 출고등록
        </button>
        <button
          type="button"
          onClick={onReview}
          className="btn btn-success-solid relative"
          title={pendingCount > 0 ? `미승인 ${pendingCount}건 검수 대기` : '출고검수'}
        >
          ✓ 출고검수
          {pendingCount > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[24px] h-6 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow ring-2 ring-white">
              {pendingCount > 99 ? '99+' : pendingCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onLease}
          className="btn btn-warning-solid"
          title="임대/기타로 출고된 항목의 회수·마무리"
        >
          🔄 임대품목 관리
        </button>
        {/* 납품확인증 스캔본은 전 직원(viewer 포함) 등록 가능 (2026-07-07 요청) */}
        <button
          type="button"
          onClick={onScan}
          className="btn btn-primary"
          title="서명받은 납품확인증 스캔본을 영업건에 첨부"
        >
          🖊 납품확인증 스캔본
        </button>
      </div>

      {/* 미승인 대기 목록 — 항상 상주 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="ui-section-title">
            검수 대기 (미승인) {pending.length > 0 && <span className="text-red-600">{pending.length}건</span>}
          </h2>
          <span className="text-xs text-slate-500">행 클릭 = 출고검수 열기</span>
        </div>
        {q.isLoading ? (
          <div className="text-sm text-slate-400 text-center py-4"><LoadingLines /></div>
        ) : pending.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-4">🎉 검수 대기 중인 출고가 없습니다.</div>
        ) : (
          <table className="data-table w-full text-[13px] [&_th]:break-normal">
            <thead>
              <tr>
                <th className="text-left w-24">출고일</th>
                <th className="text-left w-20">형태</th>
                <th className="text-left">사용처</th>
                <th className="text-left">품목</th>
                <th className="text-left w-20">등록자</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((r) => (
                <tr
                  key={r.releaseId}
                  onClick={onReview}
                  className="cursor-pointer hover:bg-emerald-50/60"
                  title="클릭하면 출고검수 팝업이 열립니다"
                >
                  <td className="text-xs whitespace-nowrap">{r.releaseDate}</td>
                  <td className="text-xs">{r.releaseType}</td>
                  <td className="max-w-xs truncate" title={r.useForLabel}>{r.useForLabel}</td>
                  <td className="text-xs text-slate-500">
                    {r.items.slice(0, 2).map((it) => `${it.itemName ?? it.freeTextName ?? it.itemCode}${it.itemSpec ? ` (${it.itemSpec})` : ''}`).join(', ')}
                    {r.items.length > 2 ? ` 외 ${r.items.length - 2}건` : ''}
                  </td>
                  <td className="text-xs">{r.requesterName ?? r.requesterUserId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 최근 승인 완료 — 참고용 10건 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="ui-section-title">최근 승인 완료</h2>
          <span className="text-xs text-slate-500">전체 이력은 [입출고내역] 탭에서</span>
        </div>
        {recentApproved.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-4">최근 승인된 출고가 없습니다.</div>
        ) : (
          <table className="data-table w-full text-[13px] [&_th]:break-normal">
            <thead>
              <tr>
                <th className="text-left w-24">출고일</th>
                <th className="text-left w-20">형태</th>
                <th className="text-left">사용처</th>
                <th className="text-left">품목</th>
                <th className="text-left w-24">승인자</th>
              </tr>
            </thead>
            <tbody>
              {recentApproved.map((r) => (
                <tr key={r.releaseId}>
                  <td className="text-xs whitespace-nowrap">{r.releaseDate}</td>
                  <td className="text-xs">{r.releaseType}</td>
                  <td className="max-w-xs truncate" title={r.useForLabel}>{r.useForLabel}</td>
                  <td className="text-xs text-slate-500">
                    {r.items.slice(0, 2).map((it) => `${it.itemName ?? it.freeTextName ?? it.itemCode}${it.itemSpec ? ` (${it.itemSpec})` : ''}`).join(', ')}
                    {r.items.length > 2 ? ` 외 ${r.items.length - 2}건` : ''}
                  </td>
                  <td className="text-xs">{r.approverName ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ========================================================================
// 탭 1 — 주요재고현황 (HDD / 기타 좌·우 분할)
// ========================================================================
function StockStatusTab() {
  const [history, setHistory] = useState<{
    itemCode: string;
    itemName: string;
  } | null>(null);
  // 숫자 칸 클릭 → 그 숫자를 이루는 건들 (v2.78.0)
  const [drill, setDrill] = useState<{
    itemCode: string;
    kind: BreakdownKind;
    shortage: boolean;
  } | null>(null);
  // 드릴다운 안의 매출번호 클릭 → 영업건 요약 (매입관리와 같은 팝업)
  const [salesId, setSalesId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['stock'],
    queryFn: () => stockApi.listStock(),
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
    );
  }
  if (query.isError) {
    return (
      <div className="bg-white rounded border border-slate-200 p-12 text-center text-red-500 text-sm">
        오류: {(query.error as Error).message}
      </div>
    );
  }

  const all = (query.data ?? []).filter((s) => s.isKeyStock);
  const hdd = all.filter((s) => s.displayGroup === 'HDD');
  const others = all.filter((s) => s.displayGroup !== 'HDD');

  return (
    <>
      {/* 범례 — 모바일에선 핵심만 (안전재고 미만 표시 안내) */}
      <div className="flex flex-wrap justify-end gap-2 md:gap-3 text-xs text-slate-500 mb-2">
        <span>
          <span className="inline-block w-2 h-2 bg-red-400 rounded-full align-middle mr-1" />
          안전재고 미만
        </span>
        <span className="hidden md:inline">
          <span className="text-blue-600 font-semibold">납품예정</span> 영업건 연동
        </span>
        <span className="hidden md:inline">
          <span className="text-emerald-600 font-semibold">입고예정</span> 발주 후 미입고
        </span>
        <span className="hidden md:inline" title="자체 조립(제조)로 만든 완제품이 현재고에 들어 있는 품목">
          🔧 재고에 제조품 포함
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StockBlock
          title="HDD"
          count={hdd.length}
          rows={hdd}
          headerClass="bg-amber-50 border-b border-amber-200 text-amber-800"
          frameClass="border-amber-300"
          onRowClick={(r) =>
            setHistory({ itemCode: r.itemCode, itemName: r.itemName ?? r.itemCode })
          }
          onNumberClick={(r, kind, shortage) =>
            setDrill({ itemCode: r.itemCode, kind, shortage: shortage ?? false })
          }
        />
        <StockBlock
          title="기타"
          count={others.length}
          rows={others}
          headerClass="bg-slate-50 border-b border-slate-200 text-slate-700"
          frameClass="border-slate-200"
          onRowClick={(r) =>
            setHistory({ itemCode: r.itemCode, itemName: r.itemName ?? r.itemCode })
          }
          onNumberClick={(r, kind, shortage) =>
            setDrill({ itemCode: r.itemCode, kind, shortage: shortage ?? false })
          }
        />
      </div>

      {/* lazy 모달 — 행 클릭 시 처음 로드 */}
      <Suspense fallback={null}>
        {history && (
          <PurchaseDetailModal
            itemCode={history.itemCode}
            itemName={history.itemName}
            displayName={history.itemName}
            variant="stock"
            onClose={() => setHistory(null)}
          />
        )}
        {/* 숫자 칸 클릭 → 그 숫자를 이루는 건들 (v2.78.0) */}
        {drill && (
          <StockBreakdownModal
            itemCode={drill.itemCode}
            kind={drill.kind}
            shortage={drill.shortage}
            onSalesClick={(id) => setSalesId(id)}
            onClose={() => setDrill(null)}
          />
        )}
        {salesId && <SalesSummaryModal salesId={salesId} onClose={() => setSalesId(null)} />}
      </Suspense>
    </>
  );
}

function StockBlock({
  title,
  count,
  rows,
  headerClass,
  frameClass,
  onRowClick,
  onNumberClick,
}: {
  title: string;
  count: number;
  rows: StockRow[];
  headerClass: string;
  frameClass: string;
  onRowClick: (r: StockRow) => void;
  /** 숫자 칸 클릭 → 그 숫자를 이루는 건들 (v2.78.0). shortage=안전재고 미달 ⚠ 에서 열림 */
  onNumberClick: (r: StockRow, kind: BreakdownKind, shortage?: boolean) => void;
}) {
  // item_group 으로 묶어서 rowspan 처리
  const groups = useMemo(() => {
    const map = new Map<string, StockRow[]>();
    for (const r of rows) {
      const key = r.itemGroup ?? '(미분류)';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return Array.from(map.entries());
  }, [rows]);

  return (
    <div className={`bg-white rounded shadow-sm border ${frameClass} overflow-hidden`}>
      <div className={`px-3 py-1.5 text-xs font-bold ${headerClass}`}>
        {title} ({count}종)
      </div>
      <div className="overflow-x-auto">
        <table className="data-table w-full text-[13px] [&_th]:break-normal">
          <thead>
            <tr>
              <th className="text-left hidden md:table-cell">품목그룹</th>
              <th className="text-left">품목</th>
              <th className="text-right">현재고</th>
              <th className="text-right hidden md:table-cell">납품예정</th>
              <th className="text-right hidden md:table-cell">입고예정</th>
              <th className="text-right hidden md:table-cell">임대</th>
              <th
                className="text-right hidden md:table-cell cursor-help"
                title="가용재고 = 지금 바로 출고할 수 있는 수량
영업건에 연결(예약)되지 않고 입고 완료된 물량만 셉니다.
아직 안 들어온 발주분(입고예정)은 포함하지 않습니다.
출고등록 승인 시 실제로 차감 가능한 수량과 같은 기준입니다."
              >
                가용 <span className="text-[11px] text-slate-500">ⓘ</span>
              </th>
              <th className="text-right hidden md:table-cell">안전</th>
              <th className="text-center">상태</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="text-center text-slate-500 text-xs py-4"
                >
                  품목이 없습니다.
                </td>
              </tr>
            )}
            {groups.map(([grp, list]) =>
              list.map((s, i) => {
                const cur = s.currentStock ?? 0;
                const safe = s.safeStock ?? 0;
                const danger = cur < safe;
                const avail = s.availableStock ?? 0;
                return (
                  <tr
                    key={s.itemCode}
                    className={`cursor-pointer hover:bg-slate-50 ${
                      danger ? 'bg-red-50' : ''
                    }`}
                    onClick={() => onRowClick(s)}
                  >
                    {i === 0 && (
                      <td
                        rowSpan={list.length}
                        className="text-slate-600 align-middle font-semibold bg-slate-50 hidden md:table-cell"
                      >
                        {grp}
                      </td>
                    )}
                    <td className="font-medium text-slate-900">
                      {s.itemName ?? s.itemCode}
                      {s.mfgStock > 0 && <MfgItemBadge count={s.mfgStock} />}
                    </td>
                    <td className="text-right num">{cur}</td>
                    <td className="text-right num text-blue-600 hidden md:table-cell">
                      <DrillNum
                        value={s.pendingDelivery ?? 0}
                        onClick={() => onNumberClick(s, 'pending')}
                        title="클릭 → 납품예정인 건 보기"
                      />
                    </td>
                    <td className="text-right num text-emerald-600 hidden md:table-cell">
                      <DrillNum
                        value={s.incoming ?? 0}
                        onClick={() => onNumberClick(s, 'incoming')}
                        title="클릭 → 들어올 발주 건 보기"
                      />
                    </td>
                    <td className="text-right num text-slate-400 hidden md:table-cell">
                      <DrillNum
                        value={s.leased ?? 0}
                        onClick={() => onNumberClick(s, 'leased')}
                        title="클릭 → 어디에 임대 중인지 보기"
                      />
                    </td>
                    <td className="text-right num font-semibold hidden md:table-cell">
                      <DrillNum
                        value={avail}
                        onClick={() => onNumberClick(s, 'available')}
                        title="클릭 → 지금 출고 가능한 물량이 어느 매입분인지 보기"
                      />
                    </td>
                    <td className="text-right num text-slate-500 hidden md:table-cell">{safe}</td>
                    <td className="text-center">
                      {danger ? (
                        // ⚠ 는 0 이어도 눌린다 — 「모자란데 발주는 했나?」가 그 자리의 질문이다.
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNumberClick(s, 'incoming', true);
                          }}
                          title="클릭 → 얼마나 모자란지 · 들어올 발주가 있는지"
                          className="text-red-600 text-xs font-bold hover:bg-red-100 rounded px-1"
                        >
                          ⚠
                        </button>
                      ) : (
                        <span className="text-emerald-600 text-xs">✓</span>
                      )}
                    </td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========================================================================
// 탭 2 — 품명&품목 마스터
// ========================================================================
const CAT_BADGE: Record<string, string> = {
  HW: 'stage-badge stage-order',
  SW: 'stage-badge stage-likely',
  Service: 'stage-badge stage-deliv',
};

function ItemMasterTab({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<ItemMasterRow | null>(null);
  // 현재고 칸 클릭 → 해당 품목 최근 입·출고 내역 모달 (수정 폼과 분리)
  const [history, setHistory] = useState<{ itemCode: string; itemName: string } | null>(null);
  // 자동 등록 행만 보기 (헤더 ⚠ 배지 클릭 토글)
  const [reviewOnly, setReviewOnly] = useState(false);
  const colState = useColState();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [colMenuOpen, setColMenuOpen] = useState(false);

  const query = useQuery({
    queryKey: ['items', { q }],
    queryFn: () => stockApi.listItems({ q: q || undefined }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const allRows = query.data ?? [];
  const reviewCount = allRows.filter((r) => r.needsReview).length;
  const baseRows = reviewOnly ? allRows.filter((r) => r.needsReview) : allRows;

  const fullColumns: ItemMasterColumn[] = useMemo(
    () => [
      {
        key: 'itemCategory',
        label: '분류',
        type: 'text',
        align: 'center',
        accessor: (r) => r.itemCategory,
        render: (r) => (
          <span className={`${CAT_BADGE[r.itemCategory] ?? ''} text-[11px]`}>
            {r.itemCategory}
          </span>
        ),
        mobileHidden: true, defaultOn: true,
      },
      {
        key: 'itemGroup',
        label: '품목그룹',
        type: 'text',
        accessor: (r) => r.itemGroup ?? '',
        mobileHidden: true, defaultOn: true,
      },
      {
        key: 'itemName',
        label: '품목명',
        type: 'text',
        accessor: (r) => r.itemName ?? '',
        render: (r) => (
          <span className="font-medium">
            {r.itemName}
            {r.mfgStock > 0 && <MfgItemBadge count={r.mfgStock} />}
          </span>
        ),
        defaultOn: true,
      },
      {
        key: 'spec',
        label: '규격',
        type: 'text',
        accessor: (r) => r.spec ?? '',
        render: (r) => (
          <span
            className="text-slate-600 max-w-xs truncate inline-block align-middle"
            title={r.spec ?? ''}
          >
            {r.spec ?? '-'}
          </span>
        ),
        mobileHidden: true, defaultOn: true,
      },
      {
        key: 'itemCode',
        label: 'P/N',
        type: 'text',
        accessor: (r) => r.itemCode,
        render: (r) => <span className="text-slate-500 font-mono">{r.itemCode}</span>,
        defaultOn: true,
      },
      {
        key: 'isKeyStock',
        label: '주요',
        type: 'text',
        align: 'center',
        accessor: (r) => (r.isKeyStock ? '★' : '-'),
        sortAccessor: (r) => (r.isKeyStock ? 1 : 0),
        render: (r) =>
          r.isKeyStock ? (
            <span className="text-red-600 font-bold">★</span>
          ) : (
            <span className="text-slate-300">-</span>
          ),
        mobileHidden: true, defaultOn: true,
      },
      {
        key: 'currentStock',
        label: '현재고',
        type: 'number',
        align: 'right',
        accessor: (r) => r.currentStock,
        render: (r) => {
          const cur = r.currentStock;
          const cls =
            cur > 0
              ? 'font-semibold text-slate-900'
              : 'text-slate-400';
          return <span className={`num ${cls}`}>{cur}</span>;
        },
        defaultOn: true,
      },
      // 매입금액 합계 — 현재 보유 재고 매입원가. viewer 미노출 (원가 마스킹).
      ...(canEdit
        ? [{
            key: 'purchaseAmount',
            label: '매입금액 합계',
            type: 'number',
            align: 'right',
            accessor: (r) => r.purchaseAmount ?? 0,
            render: (r) => (
              <span className="num font-medium text-teal-800">
                {r.purchaseAmount ? `₩${r.purchaseAmount.toLocaleString('ko-KR')}` : '-'}
              </span>
            ),
            mobileHidden: true, defaultOn: true,
          } as ItemMasterColumn]
        : []),
      {
        key: 'vendor',
        label: '벤더',
        type: 'text',
        accessor: (r) => r.vendor ?? '',
        render: (r) => <span>{r.vendor ?? '-'}</span>,
        mobileHidden: true, defaultOn: true,
      },
      {
        key: 'capacity',
        label: '용량',
        type: 'text',
        accessor: (r) => r.capacity ?? '',
        render: (r) => <span>{r.capacity ?? '-'}</span>,
        mobileHidden: true, defaultOn: true,
      },
      {
        key: 'iface',
        label: '인터페이스',
        type: 'text',
        accessor: (r) => r.iface ?? '',
        render: (r) => <span>{r.iface ?? '-'}</span>,
        mobileHidden: true, defaultOn: true,
      },
      {
        key: 'needsReview',
        label: '검토',
        toggleLabel: '추가입력 필요',
        type: 'text',
        align: 'center',
        accessor: (r) => (r.needsReview ? '⚠' : ''),
        sortAccessor: (r) => (r.needsReview ? 1 : 0),
        render: (r) =>
          r.needsReview ? (
            <span className="inline-block px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[11px] font-semibold">⚠ 추가입력</span>
          ) : (
            <span className="text-slate-300">-</span>
          ),
        defaultOn: true,
      },
    ],
    [canEdit],
  );

  // 📐 내 표 설정 prefs
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: prefsApi.getPreferences,
    staleTime: 5 * 60_000,
  });
  const patchPrefs = useMutation({
    mutationFn: (next: Partial<UserPreferences>) => prefsApi.patchPreferences(next),
    onSuccess: (newPrefs) => qc.setQueryData(['preferences'], newPrefs),
  });
  const t = useExcelModeTable({
    prefs: prefsQ.data,
    fullColumns: fullColumns as unknown as (Column<unknown> & { defaultOn: boolean })[],
    prefsKey: {
      excelMode: 'itemMasterTabExcelMode',
      columns: 'itemMasterTabColumns',
      columnsKnown: 'itemMasterTabColumnsKnown',
      order: 'itemMasterTabColumnOrder',
      widths: 'itemMasterTabColumnWidths',
    },
    patch: (next) => patchPrefs.mutate(next),
  });
  const visibleColumns = t.visibleColumns as unknown as ItemMasterColumn[];

  const visibleRows = useMemo(
    () => applyColState(baseRows, fullColumns, colState),
    [baseRows, fullColumns, colState],
  );
  const filterCount = Object.keys(colState.filters).length;

  // 매입금액 합계 — 표시된 품목 기준 (viewer 는 null 이라 0). 원가라 canEdit 일 때만 노출.
  const purchaseTotal = useMemo(
    () => visibleRows.reduce((s, r) => s + (r.purchaseAmount ?? 0), 0),
    [visibleRows],
  );

  // 검색바 자동완성 후보 — 화면 행에서 품목명·품목그룹·P/N 추출.
  const searchSuggestions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of baseRows) {
      // 검색 자동완성은 품목명·그룹·규격으로 — P/N(품목코드)은 제외
      if (r.itemName) set.add(r.itemName);
      if (r.itemGroup) set.add(r.itemGroup);
      if (r.spec) set.add(r.spec);
    }
    return [...set];
  }, [baseRows]);

  return (
    <>
      {canEdit && (
        <div className="mb-2 flex items-center justify-between bg-teal-50 border border-teal-200 rounded px-3 py-2">
          <span className="text-xs font-semibold text-teal-700">
            💰 현재 보유 재고 매입금액 합계
            <span className="ml-1 font-normal text-teal-600/80">(표시된 품목 기준 · 주요+비주요)</span>
          </span>
          <span className="text-lg font-bold text-teal-800">
            ₩{purchaseTotal.toLocaleString('ko-KR')}
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
        <SearchBar
          value={q}
          onCommit={setQ}
          placeholder="🔍 품목명 / 그룹 / 규격 검색"
          className="flex-1 sm:flex-initial w-full sm:w-80 min-w-0"
          suggestions={searchSuggestions}
        />
        <span className="text-slate-500 ml-0 sm:ml-2 whitespace-nowrap">
          {query.isLoading
            ? '…'
            : visibleRows.length === baseRows.length
              ? `${baseRows.length}종`
              : `${visibleRows.length} / ${baseRows.length}종`}
        </span>
        {reviewCount > 0 && (
          <button
            type="button"
            onClick={() => setReviewOnly((v) => !v)}
            title="매입 등록에서 자동 생성된 행 — 정보 보완 후 저장 시 자동 해제"
            className={`btn btn-sm ${reviewOnly ? 'btn-warning-solid' : 'btn-secondary'}`}
          >
            ⚠ 추가입력 필요 {reviewCount}{reviewOnly ? ' · 전체 보기' : ''}
          </button>
        )}
        <span className="text-slate-400 ml-auto hidden md:inline">
          ★ = 주요품목 (재고관리 대상) · 컬럼 헤더 클릭 → 정렬 / ▼ → 값 필터
        </span>
      </div>

      <div className="mb-2 flex justify-end items-center gap-2 text-xs">
        {(colState.sortKey || filterCount > 0 || q) && (
          <ResetButton
            onClick={() => {
              colState.resetAll();
              setQ('');
            }}
            label={`↺ 정렬·필터 초기화${filterCount > 0 ? ` (${filterCount}컬럼)` : ''}`}
            title="검색·컬럼 정렬/필터 모두 초기 상태로"
          />
        )}
        <ExcelModeGroup
          enabled={t.excelMode}
          hasPrefs={t.hasPrefs}
          userName={user?.name ?? '사용자'}
          onChange={t.setExcelMode}
          onReset={t.handleReset}
        />
      </div>

      {/* ⚙ 표시 컬럼 — 📐 내 표 설정 ON 일 때만. 모바일 hidden. */}
      {t.excelMode && (
        <div className="hidden md:block mb-2 text-xs">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setColMenuOpen((v) => !v)}
              className="text-blue-600 hover:underline"
            >
              ⚙ 표시 컬럼 ({visibleColumns.length}/{t.fullAllKeys.length}{patchPrefs.isPending ? ' · 저장 중...' : ''})
              {colMenuOpen ? ' ▲' : ' ▼'}
            </button>
            <span className="text-xs text-slate-500">
              💡 컬럼 헤더 끌어 이동 / 우측 끝 끌어 너비 조정 / 헤더 ◀ ▶ 한 칸씩 이동
            </span>
          </div>
          {colMenuOpen && (
            <div className="mt-2 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
              {fullColumns.map((c) => {
                const on = t.activeColKeys.has(c.key);
                return (
                  <label
                    key={c.key}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${
                      on ? 'bg-blue-50 text-blue-800' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => t.toggleColumn(c.key)}
                      disabled={patchPrefs.isPending}
                    />
                    <span>{c.toggleLabel ?? c.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-20">
            <tr className="text-slate-700">
              {visibleColumns.map((col, idx) => (
                <ColumnHeader
                  key={col.key}
                  column={col}
                  baseRows={baseRows}
                  // 연쇄 필터 — applyColState 와 같은 컬럼 집합
                  columns={fullColumns}
                  colState={colState}
                  excelMode={t.excelMode}
                  onReorder={t.excelMode ? t.handleReorder : undefined}
                  onMoveLeft={t.excelMode && idx > 0 ? () => t.handleMoveLeft(col.key) : undefined}
                  onMoveRight={
                    t.excelMode && idx < visibleColumns.length - 1
                      ? () => t.handleMoveRight(col.key)
                      : undefined
                  }
                  onResize={t.excelMode ? t.handleResize : undefined}
                  widthPx={t.columnWidths?.[col.key]}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && !query.data && (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-8 text-slate-400"><LoadingLines /></td>
              </tr>
            )}
            {query.isError && (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-8 text-red-500">
                  오류: {(query.error as Error).message}
                </td>
              </tr>
            )}
            {query.data && visibleRows.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-8 text-slate-400">
                  조건에 맞는 품목이 없습니다.
                </td>
              </tr>
            )}
            {visibleRows.map((it) => (
              <tr
                key={it.itemCode}
                className={`${canEdit ? 'cursor-pointer hover:bg-slate-50' : ''} ${
                  it.needsReview ? 'bg-amber-50/60' : ''
                }`}
                onClick={() => {
                  if (canEdit) setEditing(it);
                }}
              >
                {visibleColumns.map((col) => {
                  // 현재고 칸은 클릭 시 입·출고 내역(수정 폼 아님). 행 onClick 으로 전파 차단.
                  const isStockCell = col.key === 'currentStock';
                  return (
                    <td
                      key={col.key}
                      onClick={
                        isStockCell
                          ? (e) => {
                              e.stopPropagation();
                              setHistory({
                                itemCode: it.itemCode,
                                itemName: it.itemName ?? it.itemCode,
                              });
                            }
                          : undefined
                      }
                      title={isStockCell ? '클릭 → 최근 입·출고 내역' : undefined}
                      className={`px-3 py-1.5 align-middle ${
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                      } ${col.mobileHidden ? 'hidden md:table-cell' : ''} ${
                        isStockCell ? 'cursor-pointer text-blue-700 hover:bg-blue-100 hover:underline' : ''
                      }`}
                    >
                      {col.render ? col.render(it) : (col.accessor(it) ?? '-')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* lazy 모달 — 행 클릭 (수정) 시 처음 로드 */}
      <Suspense fallback={null}>
        {editing && (
          <ItemFormModal
            mode="edit"
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => setEditing(null)}
          />
        )}
        {/* 현재고 칸 클릭 → 입·출고 내역 (수정과 별개 모달) */}
        {history && (
          <PurchaseDetailModal
            itemCode={history.itemCode}
            itemName={history.itemName}
            displayName={history.itemName}
            variant="stock"
            onClose={() => setHistory(null)}
          />
        )}
      </Suspense>
    </>
  );
}
